/**
 * Datos estáticos para el formulario de perfil
 */
import { OrganizationType, CatalogSection, ThematicAxis, MaturityLevel, ParticipationModality, EventProgramParticipation } from '../types/profile.types';
// Tipos de organización (igual al form participar)
export const tiposOrganizacion = [
  'Empresa / PyME',
  'Startup / Emprendimiento',
  'Agencia / Consultora',
  'Cámara Empresarial / Sectorial',
  'Cámara de Comercio Binacional',
  'Asociación Profesional',
  'ONG / Fundación / Asociación Civil',
  'Universidad / Institución Educativa',
  'Centro Tecnológico / I+D',
  'Institución de Salud / Deportiva / Cultural / Turismo / Otra',
  'Gobierno Nacional / Provincial',
  'Gobierno Municipal',
  'Ente o Institución del Sector Público',
  'Organismo Internacional',
  'Embajada',
  'Inversionista / Aceleradora',
  'Medio de Comunicación',
  'Proveedor de Soluciones Tecnológicas'
];
// Tipos de organización (mantener para compatibilidad con tipos antiguos)
export const organizationTypes: { value: OrganizationType; label: string }[] = [
  { value: 'empresa', label: 'Empresa' },
  { value: 'ong', label: 'ONG' },
  { value: 'gobierno', label: 'Gobierno' },
  { value: 'startup', label: 'Startup' },
  { value: 'universidad', label: 'Universidad' },
  { value: 'organismo_internacional', label: 'Organismo Internacional' },
  { value: 'camara', label: 'Cámara' },
  { value: 'asociacion', label: 'Asociación' },
  { value: 'otro', label: 'Otro' }
];
// Secciones del Catálogo
export const catalogSections: { value: CatalogSection; label: string }[] = [
  { value: 'empresas_soluciones', label: 'Empresas que brindan soluciones y tecnologías' },
  { value: 'empresas_impacto', label: 'Empresas con impacto' },
  { value: 'gobiernos_innovan', label: 'Gobiernos que Innovan' }
];
// Ejes temáticos (15 ejes oficiales)
export const thematicAxes: { value: ThematicAxis; label: string }[] = [
  { value: 'gobernanza_planificacion', label: 'Gobernanza, Planificación y Gestión Pública' },
  { value: 'transformacion_digital', label: 'Transformación Digital, Inteligencia Artificial y Nuevas Tecnologías' },
  { value: 'ciberseguridad', label: 'Ciberseguridad, Confianza Digital y Protección de Datos' },
  { value: 'movilidad_transporte', label: 'Movilidad, Transporte y Logística Inteligente' },
  { value: 'economia_conocimiento', label: 'Economía del Conocimiento, Innovación Productiva y Emprendimiento' },
  { value: 'energia_recursos', label: 'Energía y Recursos Naturales' },
  { value: 'construccion_infraestructura', label: 'Construcción, Infraestructura y Desarrollo Urbano' },
  { value: 'ambiente_agua', label: 'Ambiente, Agua, Residuos y Sustentabilidad Productiva' },
  { value: 'seguridad_ciudadana', label: 'Seguridad Ciudadana, Prevención, Emergencias y Respuesta' },
  { value: 'salud_bienestar', label: 'Salud, Bienestar y Calidad de Vida' },
  { value: 'deporte_ciencias', label: 'Deporte, Ciencias del Deporte e Integración Comunitaria' },
  { value: 'alimentacion_agro', label: 'Alimentación, Agro, Producción de Alimentos y Bioeconomía' },
  { value: 'educacion_talento', label: 'Educación, Talento, Empleo y Nuevas Profesiones' },
  { value: 'ciencia_investigacion', label: 'Ciencia, Investigación, Tecnología y Transferencia (I+D)' },
  { value: 'cultura_turismo', label: 'Cultura, Turismo, Patrimonio, Medios y Economía Creativa' }
];
// Subejes por eje temático (para tooltips)
export const subejesPorEje: Record<string, { subejes: string[] }> = {
  'Gobernanza, Planificación y Gestión Pública': {
    subejes: [
      'Gobierno abierto',
      'Planificación estratégica',
      'Modernización del Estado',
      'Datos, indicadores y evidencias',
      'Participación ciudadana',
      'Gestión territorial'
    ]
  },
  'Transformación Digital, Inteligencia Artificial y Nuevas Tecnologías': {
    subejes: [
      'IA aplicada a gobiernos, empresas y comunidades',
      'IoT y sistemas conectados',
      'Ciencia de datos',
      'Automatización y robótica',
      'Cloud y edge computing',
      'Identidad digital',
      'Tecnologías emergentes'
    ]
  },
  'Ciberseguridad, Confianza Digital y Protección de Datos': {
    subejes: [
      'Ciberseguridad pública y privada',
      'Infraestructuras críticas',
      'Privacidad',
      'Gestión de riesgos digitales',
      'Normativas y estándares',
      'Resiliencia digital'
    ]
  },
  'Movilidad, Transporte y Logística Inteligente': {
    subejes: [
      'Movilidad urbana',
      'Transporte público y privado',
      'Infraestructura vial',
      'Seguridad vial',
      'Logística, última milla y cadenas de suministro',
      'Movilidad sostenible'
    ]
  },
  'Economía del Conocimiento, Innovación Productiva y Emprendimiento': {
    subejes: [
      'Startups',
      'Innovación abierta',
      'Industria 4.0',
      'Transformación de cadenas productivas',
      'Nuevos modelos de negocio',
      'Parques tecnológicos y clusters'
    ]
  },
  'Energía y Recursos Naturales': {
    subejes: [
      'Energía renovable y no renovable',
      'Hidrógeno, biocombustibles',
      'Redes inteligentes',
      'Minería, litio, recursos estratégicos',
      'Energía para la industria',
      'Eficiencia energética'
    ]
  },
  'Construcción, Infraestructura y Desarrollo Urbano': {
    subejes: [
      'Desarrollo urbano',
      'Vivienda y hábitat',
      'Construcción inteligente',
      'Arquitectura y diseño urbano',
      'Infraestructura territorial',
      'Espacio público'
    ]
  },
  'Ambiente, Agua, Residuos y Sustentabilidad Productiva': {
    subejes: [
      'Gestión del agua',
      'Gestión integral de residuos',
      'Economía circular',
      'Biodiversidad',
      'Cambio climático y resiliencia',
      'Suelo y producción sostenible'
    ]
  },
  'Seguridad Ciudadana, Prevención, Emergencias y Respuesta': {
    subejes: [
      'Seguridad ciudadana',
      'Videovigilancia y tecnología aplicada',
      'Fuerzas de seguridad',
      'Bomberos',
      'Salud prehospitalaria / SAME',
      'Gestión del riesgo',
      'Protección civil y emergencias'
    ]
  },
  'Salud, Bienestar y Calidad de Vida': {
    subejes: [
      'Salud digital',
      'Atención primaria',
      'Infraestructura sanitaria',
      'Sistemas de cuidado',
      'Prevención y estilos de vida',
      'Salud mental y bienestar'
    ]
  },
  'Deporte, Ciencias del Deporte e Integración Comunitaria': {
    subejes: [
      'Fútbol y todos los deportes',
      'Infraestructura deportiva',
      'Actividad física y salud',
      'Ciencias del deporte',
      'Industria del deporte',
      'Formación y empleo deportivo',
      'Deporte como integración comunitaria'
    ]
  },
  'Alimentación, Agro, Producción de Alimentos y Bioeconomía': {
    subejes: [
      'Agro y agroindustrias',
      'Tecnología en alimentos',
      'Nutrición y seguridad alimentaria',
      'Bioeconomía',
      'Producción sostenible',
      'Cadenas agroproductivas'
    ]
  },
  'Educación, Talento, Empleo y Nuevas Profesiones': {
    subejes: [
      'Educación formal y no formal',
      'Universidades, institutos',
      'Empleo y desarrollo de talento',
      'Formación técnica',
      'Nuevas profesiones',
      'Capacitación continua'
    ]
  },
  'Ciencia, Investigación, Tecnología y Transferencia (I+D)': {
    subejes: [
      'Centros de investigación',
      'Laboratorios',
      'Ciencia aplicada',
      'DeepTech',
      'Vinculación tecnológica',
      'Innovación científica'
    ]
  },
  'Cultura, Turismo, Patrimonio, Medios y Economía Creativa': {
    subejes: [
      'Industrias creativas',
      'Turismo y destinos',
      'Patrimonio cultural',
      'Medios de comunicación',
      'Arte y cultura',
      'Economía naranja'
    ]
  }
};
// Áreas (igual que form-participar)
export const areas = [
  'Dirección y Gobierno Corporativo',
  'Administración y Finanzas',
  'Recursos Humanos / Talento',
  'Operaciones',
  'Tecnología e Innovación',
  'Marketing, Comunicación y Ventas',
  'Desarrollo de Negocios y Estrategia',
  'Productos y Servicios',
  'Sostenibilidad y Responsabilidad Social',
  'Seguridad, Riesgo y Cumplimiento',
  'Asuntos Públicos y Relacionamiento Externo'
];
// Subáreas organizadas por área (igual que form-participar)
export const subareasPorArea: Record<string, { subareas: string[] }> = {
  'Dirección y Gobierno Corporativo': {
    subareas: [
      'Dirección General / CEO',
      'Presidencia',
      'Vicepresidencia',
      'Secretaría de Directorio',
      'Gobierno Corporativo',
      'Auditoría Interna',
      'Gestión de Riesgos',
      'Asuntos Legales / Jurídicos',
      'Compliance y Ética',
      'Relaciones Institucionales'
    ]
  },
  'Administración y Finanzas': {
    subareas: [
      'Administración General',
      'Contabilidad',
      'Finanzas',
      'Control de Gestión',
      'Tesorería',
      'Impuestos',
      'Compras y Contrataciones',
      'Gestión de Proveedores',
      'Auditoría Financiera',
      'Presupuesto y Planeamiento'
    ]
  },
  'Recursos Humanos / Talento': {
    subareas: [
      'Desarrollo de Talento',
      'Selección y Reclutamiento',
      'Capacitación y Formación',
      'Evaluaciones de Desempeño',
      'Compensaciones y Beneficios',
      'Cultura Organizacional',
      'Diversidad, Equidad e Inclusión',
      'Relaciones Laborales',
      'Clima Organizacional',
      'Bienestar y Salud Ocupacional'
    ]
  },
  'Operaciones': {
    subareas: [
      'Operaciones Generales',
      'Logística',
      'Supply Chain',
      'Gestión de Calidad',
      'Gestión de Procesos',
      'Mantenimiento',
      'Servicios Generales',
      'Producción / Planta',
      'Seguridad e Higiene',
      'Operaciones Técnicas'
    ]
  },
  'Tecnología e Innovación': {
    subareas: [
      'Sistemas / TI',
      'Infraestructura Tecnológica',
      'Desarrollo de Software',
      'Soporte Técnico',
      'Ciberseguridad',
      'Arquitectura de Datos',
      'Big Data & Analytics',
      'Inteligencia Artificial',
      'Innovación y Transformación Digital',
      'Automatización y Robótica',
      'UX/UI y Productos Digitales'
    ]
  },
  'Marketing, Comunicación y Ventas': {
    subareas: [
      'Marketing',
      'Comunicación Institucional',
      'Comunicación Interna',
      'Branding',
      'Publicidad',
      'Ventas',
      'Atención al Cliente',
      'Experiencia de Usuario / Customer Success',
      'Relaciones Públicas',
      'Eventos Corporativos',
      'Investigación de Mercados',
      'Marketing Digital & Performance'
    ]
  },
  'Desarrollo de Negocios y Estrategia': {
    subareas: [
      'Planeamiento Estratégico',
      'Nuevos Negocios',
      'Desarrollo Corporativo',
      'Innovación Empresarial',
      'Alianzas y Partnerships',
      'Gestión de Proyectos',
      'Inteligencia Competitiva',
      'Expansión Internacional'
    ]
  },
  'Productos y Servicios': {
    subareas: [
      'Gestión de Producto',
      'Desarrollo de Servicios',
      'Investigación & Desarrollo (I+D)',
      'Laboratorios',
      'Gestión de Portafolio',
      'Experiencia del Usuario'
    ]
  },
  'Sostenibilidad y Responsabilidad Social': {
    subareas: [
      'Sostenibilidad Ambiental',
      'Economía Circular',
      'Energía y Transición Verde',
      'Responsabilidad Social Empresaria (RSE)',
      'Gestión de Impacto',
      'Reportes ESG'
    ]
  },
  'Seguridad, Riesgo y Cumplimiento': {
    subareas: [
      'Seguridad Física',
      'Seguridad Informática',
      'Gestión de Riesgos',
      'Crisis y Continuidad Operativa',
      'Compliance',
      'Asuntos Regulatorios'
    ]
  },
  'Asuntos Públicos y Relacionamiento Externo': {
    subareas: [
      'Relaciones Gubernamentales',
      'Relaciones con la Comunidad',
      'Vinculación Académica',
      'Relaciones Internacionales',
      'Gestión de Stakeholders'
    ]
  }
};
// Niveles de madurez
export const maturityLevels: { value: MaturityLevel; label: string }[] = [
  { value: 'piloto', label: 'Piloto' },
  { value: 'escalado', label: 'Escalado' },
  { value: 'comercial', label: 'Comercial' }
];
// Estados de desarrollo de soluciones
export const developmentStatuses: { value: 'ideacion' | 'piloto' | 'implementada' | 'escalando'; label: string }[] = [
  { value: 'ideacion', label: 'Ideación' },
  { value: 'piloto', label: 'Piloto' },
  { value: 'implementada', label: 'Implementada' },
  { value: 'escalando', label: 'Escalando' }
];
// Públicos objetivo (multiselect)
export const targetAudiences = [
  'Gobiernos / Municipios / Provincias',
  'Empresas privadas',
  'Pymes',
  'Ciudadanos',
  'ONGs / Sociedad Civil',
  'Universidades / Centros de I+D',
  'Otros'
];
// Modelos de negocio (multiselect)
export const businessModels = [
  'B2G',
  'B2B',
  'B2C',
  'SaaS',
  'Licenciamiento',
  'Consultoría / Servicios',
  'Freemium / Suscripción',
  'Otro'
];
// Tecnologías utilizadas (multiselect + opción "otra")
export const technologies = [
  'IA / Machine Learning',
  'IoT / Sensores',
  'Big Data / Analítica',
  'Blockchain',
  'Cloud / Edge',
  'Plataformas Digitales',
  'Energía / Infraestructura Tecnológica',
  'Otro'
];
// Modalidades de participación
export const participationModalities: { value: ParticipationModality; label: string }[] = [
  { value: 'institucion_colaboradora', label: 'Institución Colaboradora (Invitados)' },
  { value: 'gobiernos_innovan_invitados', label: 'Gobiernos que Innovan (Invitados)' },
  { value: 'global_partner', label: 'Global Partner (Categoría Máxima | Visibilidad Total)' },
  { value: 'industry_partner', label: 'Industry Partner (Liderazgo Sectorial)' },
  { value: 'event_partner', label: 'Event Partner (Presencia institucional)' },
  { value: 'business_partner', label: 'Business Partner (Participación con foco en networking)' }
];
// Información de tooltips para modalidades de participación
export interface ParticipationModalityTooltip {
  dirigidoA: string;
  beneficios: string[];
}
export const participationModalityTooltips: Record<ParticipationModality, ParticipationModalityTooltip> = {
  institucion_colaboradora: {
    dirigidoA: 'Dirigido a organizaciones del sector académico, sector público y otros actores institucionales que deseen participar en el ecosistema de Smart City Expo, compartir sus iniciativas y articular con empresas, gobiernos y organismos internacionales.',
    beneficios: [
      'Publicación de iniciativas y proyectos institucionales en el Catálogo',
      'Presencia institucional en ecosistema de Smart City',
      'Derecho a postular speakers para el Congreso',
      'Organización de eventos paralelos (previa evaluación del equipo SCESDE)',
      'Acceso a pases para rondas de negocios',
      'Beneficios para alianzas con empresas locales, cámaras y universidades'
    ]
  },
  gobiernos_innovan_invitados: {
    dirigidoA: 'Dirigido a gobiernos, municipios, provincias y entidades públicas que están implementando iniciativas de innovación, transformación digital y desarrollo de ciudades inteligentes.',
    beneficios: [
      'Publicación de iniciativas y proyectos gubernamentales en el Catálogo',
      'Presencia institucional en el ecosistema de Smart City',
      'Derecho a postular speakers para el Congreso',
      'Organización de eventos paralelos (previa evaluación del equipo SCESDE)',
      'Acceso a pases para rondas de negocios',
      'Oportunidades de vinculación con empresas, universidades y organismos internacionales',
      'Visibilidad de políticas públicas y programas de innovación'
    ]
  },
  global_partner: {
    dirigidoA: 'Modalidades pensadas para empresas y organizaciones comerciales que buscan visibilidad, exhibición, generación de vínculos y posicionamiento institucional en el marco de Smart City Expo.',
    beneficios: [
      'Publicación destacada en el catálogo',
      'Máxima presencia institucional en todo el ecosistema',
      'Speaker asegurado en sesión oficial del Congreso',
      'Organización de eventos paralelos',
      'Stand preferencial en zona estratégica',
      'Derecho a activaciones de marca',
      'Pases prioritarios para rondas de negocios',
      'Acceso a eventos exclusivos de vinculación'
    ]
  },
  industry_partner: {
    dirigidoA: 'Modalidades pensadas para empresas y organizaciones comerciales que buscan visibilidad, exhibición, generación de vínculos y posicionamiento institucional en el marco de Smart City Expo.',
    beneficios: [
      'Publicación destacada dentro de una industria',
      'Máxima presencia institucional en el ecosistema de su sector',
      'Speaker asegurado en sesión temática del Congreso',
      'Stand preferencial en el sector de su industria',
      'Activaciones de marca',
      'Organización de eventos paralelos sectoriales',
      'Acceso prioritario a rondas de negocios sectoriales',
      'Acceso a eventos de vinculación'
    ]
  },
  event_partner: {
    dirigidoA: 'Modalidades pensadas para empresas y organizaciones comerciales que buscan visibilidad, exhibición, generación de vínculos y posicionamiento institucional en el marco de Smart City Expo.',
    beneficios: [
      'Publicación Nivel 2 en catálogo',
      'Presencia destacada en footer del ecosistema web',
      'Speaker garantizado en sesión paralela',
      'Espacio en zona expo para stand',
      'Organización de eventos paralelos',
      'Activaciones de marca',
      'Pases con prioridad 2 en rondas de negocios',
      'Acceso a eventos institucionales de vinculación'
    ]
  },
  business_partner: {
    dirigidoA: 'Modalidades pensadas para empresas y organizaciones comerciales que buscan visibilidad, exhibición, generación de vínculos y posicionamiento institucional en el marco de Smart City Expo.',
    beneficios: [
      'Publicación estándar en catálogo',
      'Presencia institucional en footer del ecosistema',
      'Pases a rondas de negocios',
      'Posibilidad de ampliar su participación con opciones adicionales'
    ]
  }
};
// Participación en el programa del evento
export const eventProgramParticipation: { value: EventProgramParticipation; label: string }[] = [
  { value: 'speaker', label: 'Participar como SPEAKER en una sesión oficial del Congreso' },
  { value: 'evento_paralelo', label: 'Organizar un EVENTO PARALELO (workshop, mesa redonda, demo, presentación sectorial, entre otros)' },
  { value: 'stand_expo', label: 'Disponer de un STAND en la zona expo (soluciones, instituciones, empresas, gobiernos o proyectos)' },
  { value: 'comunicacion_marca', label: 'Realizar acciones de COMUNICACIÓN y presencia de marca (activaciones especiales, señalética, copatrocinios)' },
  { value: 'rondas_negocios', label: 'Integrar rondas de negocios y networking sectorial' },
  { value: 'pases_eventos', label: 'Disponer de pases a eventos institucionales de vinculación' }
];
// Países (igual al form inscripciones)
export interface Country {
  code: string;
  name: string;
}
export const countries: Country[] = [
  // América Latina
  { code: 'AR', name: 'Argentina' },
  { code: 'BR', name: 'Brasil' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'MX', name: 'México' },
  { code: 'PE', name: 'Perú' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'PA', name: 'Panamá' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'HN', name: 'Honduras' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'CU', name: 'Cuba' },
  { code: 'DO', name: 'República Dominicana' },
  { code: 'HT', name: 'Haití' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'TT', name: 'Trinidad y Tobago' },
  // América del Norte
  { code: 'US', name: 'Estados Unidos' },
  { code: 'CA', name: 'Canadá' },
  // Europa
  { code: 'ES', name: 'España' },
  { code: 'IT', name: 'Italia' },
  { code: 'FR', name: 'Francia' },
  { code: 'DE', name: 'Alemania' },
  { code: 'GB', name: 'Reino Unido' },
  { code: 'PT', name: 'Portugal' },
  { code: 'NL', name: 'Países Bajos' },
  { code: 'BE', name: 'Bélgica' },
  { code: 'CH', name: 'Suiza' },
  { code: 'AT', name: 'Austria' },
  { code: 'SE', name: 'Suecia' },
  { code: 'NO', name: 'Noruega' },
  { code: 'DK', name: 'Dinamarca' },
  { code: 'FI', name: 'Finlandia' },
  { code: 'PL', name: 'Polonia' },
  { code: 'CZ', name: 'República Checa' },
  { code: 'HU', name: 'Hungría' },
  { code: 'RO', name: 'Rumania' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'GR', name: 'Grecia' },
  { code: 'RU', name: 'Rusia' },
  { code: 'UA', name: 'Ucrania' },
  { code: 'IE', name: 'Irlanda' },
  { code: 'IS', name: 'Islandia' },
  // Asia
  { code: 'CN', name: 'China' },
  { code: 'JP', name: 'Japón' },
  { code: 'KR', name: 'Corea del Sur' },
  { code: 'IN', name: 'India' },
  { code: 'TH', name: 'Tailandia' },
  { code: 'SG', name: 'Singapur' },
  { code: 'MY', name: 'Malasia' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'PH', name: 'Filipinas' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'TW', name: 'Taiwán' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'PK', name: 'Pakistán' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'NP', name: 'Nepal' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'KH', name: 'Camboya' },
  { code: 'LA', name: 'Laos' },
  { code: 'MN', name: 'Mongolia' },
  // Oceanía
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'Nueva Zelanda' },
  { code: 'FJ', name: 'Fiyi' },
  { code: 'PG', name: 'Papúa Nueva Guinea' },
  { code: 'NC', name: 'Nueva Caledonia' },
  // África
  { code: 'ZA', name: 'Sudáfrica' },
  { code: 'EG', name: 'Egipto' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenia' },
  { code: 'MA', name: 'Marruecos' },
  { code: 'TN', name: 'Túnez' },
  { code: 'DZ', name: 'Argelia' },
  { code: 'GH', name: 'Ghana' },
  { code: 'ET', name: 'Etiopía' },
  { code: 'UG', name: 'Uganda' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'ZW', name: 'Zimbabue' },
  { code: 'BW', name: 'Botsuana' },
  { code: 'NA', name: 'Namibia' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'MW', name: 'Malaui' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MU', name: 'Mauricio' },
  { code: 'SC', name: 'Seychelles' },
  // Oriente Medio
  { code: 'SA', name: 'Arabia Saudí' },
  { code: 'AE', name: 'Emiratos Árabes Unidos' },
  { code: 'QA', name: 'Catar' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'BH', name: 'Baréin' },
  { code: 'OM', name: 'Omán' },
  { code: 'JO', name: 'Jordania' },
  { code: 'LB', name: 'Líbano' },
  { code: 'SY', name: 'Siria' },
  { code: 'IQ', name: 'Irak' },
  { code: 'IR', name: 'Irán' },
  { code: 'IL', name: 'Israel' },
  { code: 'TR', name: 'Turquía' }
];

/** Opciones para País/Región (continentes/regiones + países, multi-selección en CRM) */
export const paisRegionOpciones = [
  'Latam',
  'América del Norte',
  'América del Sur',
  'Europa',
  'Asia',
  'África',
  'Oceanía',
  'Caribe',
  'Antártida',
  'Oriente Medio'
];

/** Lista combinada: regiones primero, luego todos los países (para el selector País/Región) */
export const paisRegionOpcionesConPaises = [
  ...paisRegionOpciones,
  ...countries.map((c) => c.name)
];

// Provincias de Argentina (puede expandirse)
export const argentinaProvinces: { value: string; label: string }[] = [
  { value: 'santiago_del_estero', label: 'Santiago del Estero' },
  { value: 'buenos_aires', label: 'Buenos Aires' },
  { value: 'catamarca', label: 'Catamarca' },
  { value: 'chaco', label: 'Chaco' },
  { value: 'chubut', label: 'Chubut' },
  { value: 'cordoba', label: 'Córdoba' },
  { value: 'corrientes', label: 'Corrientes' },
  { value: 'entre_rios', label: 'Entre Ríos' },
  { value: 'formosa', label: 'Formosa' },
  { value: 'jujuy', label: 'Jujuy' },
  { value: 'la_pampa', label: 'La Pampa' },
  { value: 'la_rioja', label: 'La Rioja' },
  { value: 'mendoza', label: 'Mendoza' },
  { value: 'misiones', label: 'Misiones' },
  { value: 'neuquen', label: 'Neuquén' },
  { value: 'rio_negro', label: 'Río Negro' },
  { value: 'salta', label: 'Salta' },
  { value: 'san_juan', label: 'San Juan' },
  { value: 'san_luis', label: 'San Luis' },
  { value: 'santa_cruz', label: 'Santa Cruz' },
  { value: 'santa_fe', label: 'Santa Fe' },
  { value: 'tierra_del_fuego', label: 'Tierra del Fuego' },
  { value: 'tucuman', label: 'Tucumán' }
];
// Industrias (igual al form participar)
export const industrias = [
  'Agroindustria, Producción de Alimentos y Bioeconomía',
  'Alimentación, Nutrición y Sistemas Alimentarios Urbanos',
  'Agua, Saneamiento y Recursos Hídricos',
  'Energía, Recursos Naturales y Transición Energética',
  'Construcción, Infraestructura y Desarrollo Urbano',
  'Arquitectura, Diseño Urbano y Construcción Sostenible',
  'Movilidad, Transporte y Logística Inteligente',
  'Tecnología, Innovación y Transformación Digital',
  'Inteligencia Artificial y Tecnologías Avanzadas',
  'Ciberseguridad y Protección de Datos',
  'Electrónica, Automatización y Robótica',
  'Manufactura, Industria 4.0 y Producción Avanzada',
  'Minería, Materiales y Recursos Naturales',
  'Economía Circular y Gestión de Residuos',
  'Medio Ambiente y Cambio Climático',
  'Salud, Medicina y Ciencias de la Vida',
  'Deporte, Ciencias del Deporte y Bienestar',
  'Educación, Formación y EdTech',
  'Cultura, Industrias Creativas y Economía Naranja',
  'Turismo, Hotelería y Gastronomía',
  'Comercio, Retail y Comercio Electrónico',
  'Finanzas, Banca, Seguros y Fintech',
  'Telecomunicaciones y Conectividad',
  'Economía Social, Cooperativas y Desarrollo Comunitario',
  'Gobierno, Políticas Públicas y Smart Governance',
  'Seguridad, Emergencias y Resiliencia Urbana',
  'Inmobiliario, Real Estate y PropTech',
  'Recursos Humanos, Talento y Desarrollo Profesional',
  'Legal, Contable y Servicios Profesionales',
  'Industrias Textil, Moda y Sostenibilidad',
  'Industria Farmacéutica y Laboratorios',
  'Nanotecnología y Ciencia de Materiales',
  'Aeronáutica, Aeroespacial y Drones',
  'Servicios Empresariales y Consultoría',
  'Industrias Creativas Digitales y Medios'
];
// Subindustrias organizadas por industria (igual al form participar)
export const subindustriasPorIndustria = {
  'Agroindustria, Producción de Alimentos y Bioeconomía': {
    subindustrias: [
      'AgTech',
      'Ganadería',
      'Producción Agroalimentaria',
      'Bioinsumos',
      'Trazabilidad'
    ]
  },
  'Alimentación, Nutrición y Sistemas Alimentarios Urbanos': {
    subindustrias: [
      'Foodtech',
      'Nutrición',
      'Seguridad Alimentaria',
      'Cadenas de Valor Alimentarias'
    ]
  },
  'Agua, Saneamiento y Recursos Hídricos': {
    subindustrias: [
      'Tratamiento de Agua',
      'Saneamiento',
      'Infraestructura Hídrica',
      'Monitoreo'
    ]
  },
  'Energía, Recursos Naturales y Transición Energética': {
    subindustrias: [
      'Energías Renovables',
      'Oil & Gas',
      'Redes Inteligentes',
      'Almacenamiento Energético'
    ]
  },
  'Construcción, Infraestructura y Desarrollo Urbano': {
    subindustrias: [
      'Obras Públicas',
      'Construcción Privada',
      'Materiales',
      'Infraestructura Urbana'
    ]
  },
  'Arquitectura, Diseño Urbano y Construcción Sostenible': {
    subindustrias: [
      'Diseño Urbano',
      'Arquitectura Sustentable',
      'Certificaciones Ambientales'
    ]
  },
  'Movilidad, Transporte y Logística Inteligente': {
    subindustrias: [
      'Movilidad Eléctrica',
      'Transporte Público',
      'ITS',
      'Logistica 4.0'
    ]
  },
  'Tecnología, Innovación y Transformación Digital': {
    subindustrias: [
      'Cloud',
      'IoT',
      'Software',
      'Hardware'
    ]
  },
  'Inteligencia Artificial y Tecnologías Avanzadas': {
    subindustrias: [
      'AI',
      'Machine Learning',
      'Deep Learning',
      'Automatización Inteligente'
    ]
  },
  'Ciberseguridad y Protección de Datos': {
    subindustrias: [
      'Ciberseguridad',
      'Gestión de Identidad',
      'Protección de Datos'
    ]
  },
  'Electrónica, Automatización y Robótica': {
    subindustrias: [
      'Robótica',
      'Automatización Industrial',
      'Sensores'
    ]
  },
  'Manufactura, Industria 4.0 y Producción Avanzada': {
    subindustrias: [
      'Fábricas Inteligentes',
      'Impresión 3D',
      'Ingeniería de Procesos'
    ]
  },
  'Minería, Materiales y Recursos Naturales': {
    subindustrias: [
      'Minería Sostenible',
      'Procesamiento de Minerales',
      'Nuevos Materiales'
    ]
  },
  'Economía Circular y Gestión de Residuos': {
    subindustrias: [
      'Reciclaje',
      'Gestión de Residuos',
      'Valorización'
    ]
  },
  'Medio Ambiente y Cambio Climático': {
    subindustrias: [
      'Mitigación',
      'Adaptación',
      'Monitoreo Ambiental'
    ]
  },
  'Salud, Medicina y Ciencias de la Vida': {
    subindustrias: [
      'Hospitales',
      'Salud Digital',
      'Biotech',
      'Equipamiento Médico'
    ]
  },
  'Deporte, Ciencias del Deporte y Bienestar': {
    subindustrias: [
      'Clubes',
      'Fitness',
      'eSports',
      'Tecnología Deportiva'
    ]
  },
  'Educación, Formación y EdTech': {
    subindustrias: [
      'Instituciones Educativas',
      'EdTech',
      'Capacitación'
    ]
  },
  'Cultura, Industrias Creativas y Economía Naranja': {
    subindustrias: [
      'Audiovisual',
      'Videojuegos',
      'Música',
      'Diseño'
    ]
  },
  'Turismo, Hotelería y Gastronomía': {
    subindustrias: [
      'Hotelería',
      'Gastronomía',
      'Turismo Inteligente'
    ]
  },
  'Comercio, Retail y Comercio Electrónico': {
    subindustrias: [
      'RetailTech',
      'E-commerce',
      'Centros Comerciales'
    ]
  },
  'Finanzas, Banca, Seguros y Fintech': {
    subindustrias: [
      'Fintech',
      'Seguros',
      'Banca Digital'
    ]
  },
  'Telecomunicaciones y Conectividad': {
    subindustrias: [
      '5G',
      'Fibra Óptica',
      'Satélites',
      'Proveedores de Internet'
    ]
  },
  'Economía Social, Cooperativas y Desarrollo Comunitario': {
    subindustrias: [
      'Cooperativas',
      'ONGs',
      'Desarrollo Social'
    ]
  },
  'Gobierno, Políticas Públicas y Smart Governance': {
    subindustrias: [
      'Gobierno Digital',
      'Datos Abiertos',
      'Servicios Públicos'
    ]
  },
  'Seguridad, Emergencias y Resiliencia Urbana': {
    subindustrias: [
      'Seguridad Ciudadana',
      'Bomberos',
      'Emergencias Médicas',
      'Defensa Civil'
    ]
  },
  'Inmobiliario, Real Estate y PropTech': {
    subindustrias: [
      'Desarrollos Inmobiliarios',
      'PropTech',
      'Gestión de Activos'
    ]
  },
  'Recursos Humanos, Talento y Desarrollo Profesional': {
    subindustrias: [
      'Consultoras RRHH',
      'Capacitación Ejecutiva',
      'Reclutamiento'
    ]
  },
  'Legal, Contable y Servicios Profesionales': {
    subindustrias: [
      'Estudios Jurídicos',
      'Estudios Contables',
      'Asesorias'
    ]
  },
  'Industrias Textil, Moda y Sostenibilidad': {
    subindustrias: [
      'Moda Sostenible',
      'Producción Textil',
      'Innovación Textil'
    ]
  },
  'Industria Farmacéutica y Laboratorios': {
    subindustrias: [
      'Laboratorios',
      'Investigación Clínica',
      'Desarrollo Farmacéutico'
    ]
  },
  'Nanotecnología y Ciencia de Materiales': {
    subindustrias: [
      'Nanomateriales',
      'Laboratorios',
      'Aplicaciones Industriales'
    ]
  },
  'Aeronáutica, Aeroespacial y Drones': {
    subindustrias: [
      'Drones',
      'Aeronáutica',
      'Aeroespacial'
    ]
  },
  'Servicios Empresariales y Consultoría': {
    subindustrias: [
      'Consultorías',
      'Auditoría',
      'Servicios BPO'
    ]
  },
  'Industrias Creativas Digitales y Medios': {
    subindustrias: [
      'Agencias Digitales',
      'Medios',
      'Publicidad'
    ]
  }
};
// Perfiles de asistentes por eje temático (extraídos de TEMATICAS/*.html)
export const attendeeProfilesByThematicAxis: Record<string, string[]> = {
  'Gobernanza, Planificación y Gestión Pública': [
    'Intendentes, gobernadores, ministros, secretarios y subsecretarios.',
    'Jefes de gabinete, coordinadores de programas y equipos de gestión.',
    'Directores de modernización, gobierno digital, planificación y gestión territorial.',
    'Analistas de políticas públicas, politólogos, sociólogos y especialistas en gestión pública.',
    'Técnicos municipales y provinciales responsables de programas y proyectos.',
    'Legisladores, concejales y asesores parlamentarios.',
    'Representantes de organizaciones sociales y comunitarias.',
    'Investigadores y estudiantes avanzados en administración pública y ciencias sociales.'
  ],
  'Transformación Digital, Inteligencia Artificial y Nuevas Tecnologías': [
    'CIO, CTO, CDO, responsables de infraestructura, sistemas e innovación.',
    'Ingenieros de software, desarrolladores, arquitectos de soluciones, devops.',
    'Especialistas en IA, machine learning, ciencia de datos y analítica avanzada.',
    'Responsables de innovación en grandes empresas, bancos, utilities y gobiernos.',
    'Emprendedores tecnológicos, fundadores de startups, aceleradoras e incubadoras.',
    'Docentes y estudiantes de ingeniería, informática y disciplinas afines.',
    'Consultores tecnológicos y estrategas de transformación digital.'
  ],
  'Ciberseguridad, Confianza Digital y Protección de Datos': [
    'CISOs y responsables de seguridad informática.',
    'Analistas SOC/NOC, administradores de redes y sistemas.',
    'Responsables de tecnologías en bancos, utilities, telecom y salud.',
    'Auditores de sistemas, especialistas en compliance y gestión de riesgos.',
    'Funcionarios de fuerzas de seguridad y defensa cibernética.',
    'Consultores en ciberseguridad, arquitectos de soluciones, técnicos especializados.'
  ],
  'Movilidad, Transporte y Logística Inteligente': [
    'Secretarios y directores de transporte, tránsito, obras públicas y planificación urbana.',
    'Ingenieros viales, de transporte y logística.',
    'Gerentes de empresas de transporte y logística.',
    'Responsables de movilidad corporativa en grandes empresas e industrias.',
    'Urbanistas, geógrafos, planificadores y consultores.',
    'Representantes de cámaras de transporte y logística.',
    'Startups de movilidad y logística inteligente.'
  ],
  'Economía del Conocimiento, Innovación Productiva y Emprendimiento': [
    'Emprendedores, fundadores de startups, equipos de innovación.',
    'CEOs, COOs, CIOs, directores de transformación digital e industria 4.0.',
    'Responsables de desarrollo de nuevos negocios y corporate innovation.',
    'Inversores ángeles, fondos de VC, aceleradoras e incubadoras.',
    'Consultores en desarrollo productivo, industria y emprendimiento.',
    'Profesionales y técnicos de parques industriales y tecnológicos.'
  ],
  'Energía y Recursos Naturales': [
    'Ingenieros eléctricos, mecánicos, químicos y mineros.',
    'Gerentes de planta, directores de operaciones y responsables de energía en industrias.',
    'Ejecutivos de empresas energéticas, mineras y proveedoras de tecnología.',
    'Funcionarios de gobiernos nacionales y provinciales en energía, minería y ambiente.',
    'Consultores en energía, recursos naturales y transición energética.',
    'Investigadores y académicos especializados en temas energéticos y ambientales.'
  ],
  'Construcción, Infraestructura y Desarrollo Urbano': [
    'Arquitectos y urbanistas.',
    'Ingenieros civiles, hidráulicos, estructurales, industriales.',
    'Desarrolladores inmobiliarios y directores de proyectos.',
    'Funcionarios de obras públicas, vivienda y desarrollo urbano.',
    'Técnicos de obra, inspectores y supervisores.',
    'Consultores y especialistas en planificación territorial.'
  ],
  'Ambiente, Agua, Residuos y Sustentabilidad Productiva': [
    'Ingenieros ambientales, sanitarios, químicos y civiles.',
    'Biólogos, ecólogos, geógrafos y especialistas en biodiversidad.',
    'Técnicos y gestores de residuos, agua y saneamiento.',
    'Responsables de medio ambiente en empresas.',
    'Funcionarios ambientales, directores de áreas de gestión hídrica y climática.',
    'Líderes de cooperativas de recicladores y organizaciones sociales.'
  ],
  'Seguridad Ciudadana, Prevención, Emergencias y Respuesta': [
    'Jefes y oficiales de fuerzas de seguridad.',
    'Comandantes y oficiales de bomberos y brigadas de rescate.',
    'Directores de emergencias médicas y coordinadores de SAME/Urgencias.',
    'Técnicos y operadores de centros de monitoreo urbano.',
    'Funcionarios responsables de seguridad ciudadana y protección civil.',
    'Proveedores y técnicos de soluciones tecnológicas para seguridad.'
  ],
  'Salud, Bienestar y Calidad de Vida': [
    'Médicos y médicas de distintas especialidades.',
    'Enfermeros, enfermeras y personal de cuidado.',
    'Kinesiólogos, fisioterapeutas, nutricionistas y otros profesionales de la salud.',
    'Psicólogos, psiquiatras y profesionales de salud mental.',
    'Directores de hospitales, gerentes de clínicas y responsables de gestión sanitaria.',
    'Técnicos en imágenes, laboratorio, electromedicina y áreas afines.',
    'Funcionarios y técnicos de ministerios y secretarías de salud.'
  ],
  'Deporte, Ciencias del Deporte e Integración Comunitaria': [
    'Dirigentes de clubes, federaciones y ligas deportivas.',
    'Entrenadores, directores técnicos y coordinadores deportivos.',
    'Preparadores físicos, kinesiólogos, fisioterapeutas, médicos del deporte.',
    'Analistas de rendimiento, scouts, ojeadores y data scientists deportivos.',
    'Profesores y profesoras de educación física.',
    'Gestores de infraestructura deportiva y responsables de programas comunitarios.',
    'Estudiantes de carreras vinculadas al deporte, educación física, salud y gestión.'
  ],
  'Alimentación, Agro, Producción de Alimentos y Bioeconomía': [
    'Productores, agrónomos, veterinarios y técnicos agropecuarios.',
    'Gerentes de planta, responsables de calidad, logística y operaciones en agroindustrias.',
    'Emprendedores y desarrolladores de soluciones AgTech.',
    'Funcionarios públicos de áreas agropecuarias y producción.',
    'Investigadores y técnicos en biotecnología, bioeconomía y alimentos.',
    'Profesionales de nutrición, salud y programas alimentarios.'
  ],
  'Educación, Talento, Empleo y Nuevas Profesiones': [
    'Directivos y responsables de instituciones educativas.',
    'Docentes, formadores y tutores.',
    'Equipos de recursos humanos, talento y capacitación en empresas.',
    'Jóvenes, estudiantes y egresados en búsqueda de desarrollo profesional.',
    'Emprendedores y trabajadores en procesos de reconversión laboral.',
    'Investigadores en educación, trabajo y futuro del empleo.'
  ],
  'Ciencia, Investigación, Tecnología y Transferencia (I+D)': [
    'Investigadores, científicos, doctores y becarios.',
    'Tecnólogos, ingenieros, desarrolladores de producto.',
    'Gestores de I+D, responsables de innovación corporativa.',
    'Directivos de organismos científicos y universidades.',
    'Representantes de agencias de financiamiento y programas de ciencia.',
    'Emprendedores científicos y startups tecnológicas.'
  ],
  'Cultura, Turismo, Patrimonio, Medios y Economía Creativa': [
    'Gestores culturales, programadores y directores de espacios culturales.',
    'Operadores turísticos, agentes de viaje, empresarios hoteleros y gastronómicos.',
    'Productores y realizadores audiovisuales, guionistas, directores y técnicos.',
    'Periodistas, comunicadores, community managers y responsables de medios.',
    'Artistas, músicos, diseñadores, ilustradores, desarrolladores de videojuegos y creativos.',
    'Funcionarios de áreas de cultura y turismo a nivel nacional, provincial y municipal.'
  ]
};
// Estado actual (común para todas las secciones del catálogo)
export const currentStatuses: { value: 'en_diseno' | 'piloto' | 'implementado' | 'escalando' | ''; label: string }[] = [
  { value: '', label: 'Seleccionar estado...' },
  { value: 'en_diseno', label: 'En diseño' },
  { value: 'piloto', label: 'Piloto' },
  { value: 'implementado', label: 'Implementado' },
  { value: 'escalando', label: 'Escalando' }
];
// Tipo de solución (específico para Soluciones)
export const solutionTypes: { value: 'software' | 'hardware' | 'infraestructura' | 'servicio' | 'metodologico' | 'hibrido' | 'otro' | ''; label: string }[] = [
  { value: '', label: 'Seleccionar tipo...' },
  { value: 'software', label: 'Software / Plataforma Digital' },
  { value: 'hardware', label: 'Hardware / Dispositivo' },
  { value: 'infraestructura', label: 'Infraestructura / Obra' },
  { value: 'servicio', label: 'Servicio Profesional' },
  { value: 'metodologico', label: 'Modelo / Framework metodológico' },
  { value: 'hibrido', label: 'Híbrido' },
  { value: 'otro', label: 'Otro' }
];
// Impacto esperado (específico para Desafíos)
export const expectedImpacts: { value: 'social' | 'ambiental' | 'economico' | 'tecnologico' | 'institucional' | 'urbano' | 'otro' | ''; label: string }[] = [
  { value: '', label: 'Seleccionar impacto...' },
  { value: 'social', label: 'Social' },
  { value: 'ambiental', label: 'Ambiental' },
  { value: 'economico', label: 'Económico' },
  { value: 'tecnologico', label: 'Tecnológico' },
  { value: 'institucional', label: 'Institucional / Gobernanza' },
  { value: 'urbano', label: 'Urbano – Territorial' },
  { value: 'otro', label: 'Otro' }
];
// Necesidades específicas (específico para Desafíos - Multiselect)
export const specificNeeds = [
  'Tecnología / Innovación',
  'Financiamiento',
  'Alianzas estratégicas',
  'Talento / Formación',
  'Regulación / normativa',
  'Infraestructura',
  'Otro'
];
// Número estimado de beneficiarios (específico para Programas de Impacto)
export const estimatedBeneficiaries: { value: 'hasta_500' | '500_5000' | '5000_50000' | 'mas_50000' | 'no_aplica' | ''; label: string }[] = [
  { value: '', label: 'Seleccionar rango...' },
  { value: 'hasta_500', label: 'Hasta 500' },
  { value: '500_5000', label: '500 – 5.000' },
  { value: '5000_50000', label: '5.000 – 50.000' },
  { value: 'mas_50000', label: 'Más de 50.000' },
  { value: 'no_aplica', label: 'No aplica / No medido' }
];
// Tipo de política / instrumento (específico para Políticas Públicas)
export const policyTypes: { value: 'programa' | 'piloto' | 'politica' | 'estrategia' | 'servicio' | 'infraestructura' | 'otro' | ''; label: string }[] = [
  { value: '', label: 'Seleccionar tipo...' },
  { value: 'programa', label: 'Programa público' },
  { value: 'piloto', label: 'Proyecto piloto' },
  { value: 'politica', label: 'Política pública / normativa' },
  { value: 'estrategia', label: 'Estrategia / Plan' },
  { value: 'servicio', label: 'Servicio público digital' },
  { value: 'infraestructura', label: 'Infraestructura / equipamiento' },
  { value: 'otro', label: 'Otro' }
];
// Tecnologías utilizadas (actualizado para Soluciones)
export const technologiesUsed = [
  'IA / Machine Learning',
  'IoT / Sensores',
  'Big Data / Analítica',
  'Blockchain',
  'Cloud / Edge',
  'Automatización / Robótica',
  'Energía / Smart Infrastructure',
  'Otro'
];
